import supabase from "./supabaseClient.js";
import {
  calculateWorkshopDerived,
  StudentRegistrationFormSchema,
} from "../types/workshop.js";

/** 
 * ============================================================
 * WORKSHOPS SERVICE (Pure Supabase Single Source of Truth)
 * ============================================================
 * Production service layer interfacing exclusively with:
 * - Supabase PostgreSQL database
 * - Supabase Storage (`workshop-resumes`)
 * - Atomic `register_for_workshop` PostgreSQL RPC
 *
 * NOTE: ZERO mock data or localStorage fallbacks exist.
 * If Supabase encounters an error, it is surfaced directly
 * to the UI layer for appropriate error boundary handling.
 * ============================================================
 */

/**
 * Normalizes a Supabase PostgreSQL row into the schema expected by the UI.
 * Attaches calculated derived fields (spots_remaining, is_registration_open, etc.).
 */
export function mapDatabaseWorkshopToUI(row) {
  if (!row) return null;

  const category = row.workshop_categories?.name || row.category || "General";

  // Build instructors list from the junction table (if present), else fall back to direct FK
  const instructors = (() => {
    if (Array.isArray(row.workshop_instructor_links) && row.workshop_instructor_links.length > 0) {
      return [...row.workshop_instructor_links]
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((link) => {
          const inst = link.workshop_instructors || {};
          return {
            id: inst.id || "",
            name: inst.name || "",
            role: inst.role || "",
            bio: inst.bio || "",
            avatar_url: inst.avatar_url || "",
            linkedin_url: inst.linkedin_url || "",
            is_lead: Boolean(link.is_lead),
            display_order: link.display_order ?? 0,
          };
        });
    }
    // Fallback: direct FK instructor
    if (row.workshop_instructors) {
      return [{
        id: row.workshop_instructors.id,
        name: row.workshop_instructors.name,
        role: row.workshop_instructors.role,
        bio: row.workshop_instructors.bio || "",
        avatar_url: row.workshop_instructors.avatar_url || "",
        linkedin_url: row.workshop_instructors.linkedin_url || "",
        is_lead: true,
        display_order: 0,
      }];
    }
    return [];
  })();

  // Primary instructor = the lead, or first in list
  const instructor = instructors.find((i) => i.is_lead) || instructors[0] || {
    name: "Shiveda AI Engineering Team",
    role: "Senior AI Engineers & Architects",
    bio: "",
    avatar_url: "",
    linkedin_url: "",
  };

  const agenda = Array.isArray(row.workshop_agenda)
    ? [...row.workshop_agenda]
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((item) => ({
          topic: item.topic || "",
          description: item.description || "",
        }))
    : row.agenda || [];

  const allMedia = Array.isArray(row.workshop_media)
    ? [...row.workshop_media].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    : [];

  // Ordinary pre-event gallery media items: strictly media_type === 'gallery'
  const gallery = allMedia
    .filter((m) => m.media_type === "gallery")
    .map((m) => ({
      id: m.id,
      url: m.storage_path || m.url || "",
      storage_path: m.storage_path || m.url || "",
      image_url: m.storage_path || m.url || "",
      media_type: "gallery",
      caption: m.caption || "",
      title: m.caption || m.alt_text || "",
      alt_text: m.alt_text || "",
      display_order: Number(m.display_order ?? 0),
      is_published: m.is_published !== false,
    }));

  // Post-event session highlights: strictly media_type === 'highlight'
  // Preserves full fidelity for public lightbox viewer and admin studio
  const highlights = allMedia
    .filter((m) => m.media_type === "highlight")
    .map((m) => {
      const storagePath = m.storage_path || m.url || "";
      const isVideo = m.media_format === "video" || Boolean(m.video_url);
      return {
        id: m.id,
        workshop_id: m.workshop_id || row.id,
        media_type: "highlight",
        media_format: isVideo ? "video" : "image",
        url: storagePath,
        image_url: storagePath,
        storage_path: storagePath,
        video_url: m.video_url || "",
        thumbnail_url: m.thumbnail_url || (isVideo ? storagePath : ""),
        alt_text: m.alt_text || "",
        caption: m.caption || "",
        title: m.caption || m.alt_text || "Session Highlight",
        description: m.description || "",
        display_order: Number(m.display_order ?? 0),
        is_published: m.is_published !== false,
        is_featured: Boolean(m.is_featured),
      };
    });

  // Confidential meeting link is segregated in workshop_internal_data (admin only)
  // Public queries NEVER join or return workshop_internal_data
  const meeting_link = row.workshop_internal_data?.meeting_link || "";

  const baseWorkshop = {
    id: row.id,
    title: row.title,
    slug: row.slug,
    short_description: row.short_description || "",
    description: row.description || "",
    category,
    category_id: row.category_id,
    mode: row.mode || "Online",
    venue: row.venue || "",
    meeting_link,
    date: row.date,
    start_time: row.start_time,
    end_time: row.end_time,
    timezone: row.timezone || "IST (UTC+5:30)",
    duration: row.duration || "",
    instructor,
    instructors,
    cover_image: row.cover_image || "",
    gallery,
    highlights,
    show_in_promotional_modal: Boolean(row.show_in_promotional_modal),
    show_highlights: Boolean(row.show_highlights),
    learning_outcomes: row.learning_outcomes || [],
    agenda,
    prerequisites: row.prerequisites || [],
    requirements: row.requirements || [],
    certificate_available: Boolean(row.certificate_available),
    capacity: Number(row.capacity) || 0,
    registration_count: Number(row.registration_count) || 0,
    is_free: Boolean(row.is_free),
    price: Number(row.price) || 0,
    registration_deadline: row.registration_deadline || "",
    status: row.status || "Draft",
    is_published: Boolean(row.is_published),
    is_featured: Boolean(row.is_featured),
    display_order: Number(row.display_order ?? 0),
    meta_title: row.meta_title || "",
    meta_description: row.meta_description || "",
    created_at: row.created_at,
    updated_at: row.updated_at,
  };

  const derived = calculateWorkshopDerived(baseWorkshop);
  return {
    ...baseWorkshop,
    ...derived,
    // Strictly assign highlights: empty if ineligible, or published highlights only
    highlights: derived.is_highlights_eligible ? (derived.public_highlights || []) : [],
  };
}

export const workshopsService = {
  /**
   * ============================================================
   * 1. PUBLIC API METHODS
   * ============================================================
   */

  /**
   * Fetch all published workshops matching optional filters from Supabase.
   */
  async getWorkshops(filters = {}) {
    let query = supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_media (*)
      `)
      .is("deleted_at", null);

    // Public site: strictly published/completed and non-deleted
    if (!filters.includeUnpublished) {
      query = query.eq("is_published", true).in("status", ["Published", "Completed"]);
    }

    if (filters.status && filters.status !== "All") {
      query = query.eq("status", filters.status);
    }

    if (filters.mode && filters.mode !== "All") {
      query = query.eq("mode", filters.mode);
    }

    query = query.order("display_order", { ascending: true });

    const { data, error } = await query;
    if (error) {
      console.error("[workshopsService] getWorkshops error:", error);
      throw error;
    }

    let mapped = (data || []).map(mapDatabaseWorkshopToUI);

    // Filter by category name
    if (filters.category && filters.category !== "All") {
      mapped = mapped.filter((w) => w.category === filters.category);
    }

    // Text search filter
    if (filters.search && filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      mapped = mapped.filter(
        (w) =>
          w.title?.toLowerCase().includes(q) ||
          w.short_description?.toLowerCase().includes(q) ||
          w.instructor?.name?.toLowerCase().includes(q)
      );
    }

    return mapped;
  },

  /**
   * Fetch a single workshop by slug from Supabase.
   * Public visibility: strictly published workshops (allowDraft = false).
   */
  async getWorkshopBySlug(slug, allowDraft = false) {
    if (!slug) return null;

    let query = supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_agenda (*),
        workshop_media (*)
      `)
      .eq("slug", slug)
      .is("deleted_at", null);

    if (!allowDraft) {
      query = query.eq("is_published", true).in("status", ["Published", "Completed"]);
    }

    const { data, error } = await query.maybeSingle();
    if (error) {
      console.error("[workshopsService] getWorkshopBySlug error:", error);
      throw error;
    }

    return data ? mapDatabaseWorkshopToUI(data) : null;
  },

  /**
   * Fetch the featured workshop from Supabase for spotlight banner.
   */
  async getFeaturedWorkshop() {
    const { data, error } = await supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_media (*)
      `)
      .eq("is_published", true)
      .in("status", ["Published", "Completed"])
      .eq("is_featured", true)
      .is("deleted_at", null)
      .order("display_order", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("[workshopsService] getFeaturedWorkshop error:", error);
      throw error;
    }

    return data ? mapDatabaseWorkshopToUI(data) : null;
  },

  /**
   * Submit student registration (strictly the 5 approved student fields).
   * Executes atomic database RPC `register_for_workshop` with row locking.
   */
  async submitRegistration(workshopId, studentData, resumeUrl = "", resumeFilename = "") {
    if (!workshopId) {
      throw new Error("Workshop ID is required for registration");
    }

    // 1. Client schema validation (5 approved fields only)
    const validationResult = StudentRegistrationFormSchema.safeParse(studentData);
    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0]?.message || "Validation failed";
      throw new Error(firstError);
    }

    if (!resumeUrl || !resumeUrl.trim()) {
      throw new Error("Resume upload path is required for registration");
    }

    // 2. Invoke Atomic Supabase RPC (with pessimistic row-level lock)
    const { data, error } = await supabase.rpc("register_for_workshop", {
      p_workshop_id: workshopId,
      p_name: studentData.name.trim(),
      p_email: studentData.email.trim().toLowerCase(),
      p_resume_url: resumeUrl.trim(),
      p_resume_filename: resumeFilename || studentData.resume?.name || "resume.pdf",
      p_college: studentData.college.trim(),
      p_residing_city: studentData.residing_city.trim(),
      p_residing_state: studentData.residing_state.trim(),
    });

    if (error) {
      console.error("[workshopsService] RPC register_for_workshop error:", error.message);
      throw new Error(error.message);
    }

    if (data && data.success) {
      return {
        success: true,
        registrationId: data.registration_id,
        registration: data,
        message: "Your registration has been submitted successfully! We will review your application shortly.",
      };
    }

    throw new Error(data?.message || "Registration submission failed");
  },

  /**
   * ============================================================
   * 2. ADMIN & DATA MANAGEMENT METHODS
   * ============================================================
   */

  /**
   * Fetch all workshops for admin dashboard (all statuses).
   */
  async getAdminWorkshops() {
    const { data, error } = await supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_media (*),
        workshop_internal_data (meeting_link, internal_notes)
      `)
      .is("deleted_at", null)
      .order("display_order", { ascending: true });

    if (error) {
      console.error("[workshopsService] getAdminWorkshops error:", error);
      throw error;
    }

    return (data || []).map(mapDatabaseWorkshopToUI);
  },

  /**
   * Fetch a single workshop by ID (for admin editor).
   */
  async getWorkshopById(id) {
    if (!id) return null;

    const { data, error } = await supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_agenda (*),
        workshop_media (*),
        workshop_internal_data (meeting_link, internal_notes)
      `)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[workshopsService] getWorkshopById error:", error);
      throw error;
    }

    return data ? mapDatabaseWorkshopToUI(data) : null;
  },

  /**
   * Fetch a single workshop by slug or UUID (supporting human-readable routes).
   */
  async getWorkshopBySlugOrId(slugOrId, allowDraft = true) {
    if (!slugOrId) return null;
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId);
    if (isUuid) {
      return this.getWorkshopById(slugOrId);
    }

    let query = supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_agenda (*),
        workshop_media (*),
        workshop_internal_data (meeting_link, internal_notes)
      `)
      .eq("slug", slugOrId)
      .is("deleted_at", null);

    if (!allowDraft) {
      query = query.eq("is_published", true).in("status", ["Published", "Completed"]);
    }

    const { data, error } = await query.maybeSingle();
    if (error) {
      console.error("[workshopsService] getWorkshopBySlugOrId error:", error);
      throw error;
    }

    return data ? mapDatabaseWorkshopToUI(data) : null;
  },

  /**
   * Upsert an instructor profile into workshop_instructors.
   */
  async upsertInstructor(instructorData) {
    if (!instructorData || !instructorData.name) {
      throw new Error("Instructor name is required");
    }

    const payload = {
      name: instructorData.name.trim(),
      role: instructorData.role?.trim() || "Senior AI Engineers & Architects",
      bio: instructorData.bio?.trim() || "",
      avatar_url: instructorData.avatar_url?.trim() || "",
      linkedin_url: instructorData.linkedin_url?.trim() || "",
    };

    if (instructorData.id) {
      payload.id = instructorData.id;
    }

    const { data, error } = await supabase
      .from("workshop_instructors")
      .upsert(payload)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] upsertInstructor error:", error);
      throw error;
    }

    return data;
  },

  /**
   * Create a new workshop in Supabase.
   */
  async createWorkshop(workshopData) {
    const slug =
      workshopData.slug ||
      workshopData.title
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    const payload = {
      title: workshopData.title,
      slug,
      short_description: workshopData.short_description || "",
      description: workshopData.description || "",
      category_id: workshopData.category_id,
      mode: workshopData.mode || "Online",
      venue: workshopData.venue || "",
      date: workshopData.date,
      start_time: workshopData.start_time,
      end_time: workshopData.end_time,
      timezone: workshopData.timezone || "IST (UTC+5:30)",
      duration: workshopData.duration,
      instructor_id: workshopData.instructor_id,
      cover_image: workshopData.cover_image,
      learning_outcomes: workshopData.learning_outcomes || [],
      prerequisites: workshopData.prerequisites || [],
      requirements: workshopData.requirements || [],
      certificate_available: Boolean(workshopData.certificate_available),
      capacity: Number(workshopData.capacity) || 150,
      is_free: Boolean(workshopData.is_free),
      price: Number(workshopData.price) || 0,
      registration_deadline: workshopData.registration_deadline || null,
      status: workshopData.status || "Draft",
      is_published: workshopData.status === "Published",
      is_featured: Boolean(workshopData.is_featured),
      show_in_promotional_modal: Boolean(workshopData.show_in_promotional_modal),
      show_highlights: Boolean(workshopData.show_highlights),
    };

    const { data, error } = await supabase
      .from("workshops")
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] createWorkshop error:", error);
      throw error;
    }

    return mapDatabaseWorkshopToUI(data);
  },

  /**
   * Update an existing workshop in Supabase.
   */
  async updateWorkshop(id, workshopData) {
    if (!id) throw new Error("Workshop ID is required");

    const payload = { ...workshopData, updated_at: new Date().toISOString() };
    delete payload.id;
    delete payload.instructor;
    delete payload.instructors;
    delete payload.category;
    delete payload.agenda;
    delete payload.gallery;
    delete payload.highlights;
    delete payload.spots_remaining;
    delete payload.is_sold_out;
    delete payload.is_registration_open;
    delete payload.is_registration_closed;
    delete payload.is_live;
    delete payload.is_upcoming;
    delete payload.is_completed;
    delete payload.lifecycle_stage;
    delete payload.start_date_obj;
    delete payload.end_date_obj;
    delete payload.is_highlights_eligible;

    const { data, error } = await supabase
      .from("workshops")
      .update(payload)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] updateWorkshop error:", error);
      throw error;
    }

    return mapDatabaseWorkshopToUI(data);
  },

  /**
   * Delete a workshop.
   * If permanent is true (default), permanently removes the workshop and all cascading records.
   * If permanent is false, soft-deletes by setting deleted_at.
   */
  async deleteWorkshop(id, { permanent = true } = {}) {
    if (!id) throw new Error("Workshop ID is required");

    if (permanent) {
      const { error } = await supabase
        .from("workshops")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("[workshopsService] deleteWorkshop (permanent) error:", error);
        throw error;
      }

      return { success: true, id, permanent: true };
    }

    const { error } = await supabase
      .from("workshops")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      console.error("[workshopsService] deleteWorkshop (soft) error:", error);
      throw error;
    }

    return { success: true, id, permanent: false };
  },

  /**
   * Update workshop status lifecycle (Draft, Published, Completed, Cancelled).
   */
  async updateWorkshopStatus(id, status) {
    return this.updateWorkshop(id, {
      status,
      is_published: status === "Published",
    });
  },

  /**
   * Fetch registrations for a specific workshop from Supabase (accepts UUID or slug).
   * Generates short-lived signed URLs for private Supabase Storage resumes.
   */
  async getWorkshopRegistrations(workshopIdOrSlug) {
    let workshopId = workshopIdOrSlug;
    if (workshopIdOrSlug) {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(workshopIdOrSlug);
      if (!isUuid) {
        const { data: w } = await supabase
          .from("workshops")
          .select("id")
          .eq("slug", workshopIdOrSlug)
          .is("deleted_at", null)
          .maybeSingle();
        if (w?.id) {
          workshopId = w.id;
        }
      }
    }

    let query = supabase
      .from("workshop_registrations")
      .select("*")
      .order("created_at", { ascending: false });

    if (workshopId) {
      query = query.eq("workshop_id", workshopId);
    }

    const { data, error } = await query;
    if (error) {
      console.error("[workshopsService] getWorkshopRegistrations error:", error);
      throw error;
    }

    if (data && Array.isArray(data) && data.length > 0) {
      const withSignedUrls = await Promise.all(
        data.map(async (reg) => {
          if (
            reg.resume_url?.startsWith("resumes/") ||
            reg.resume_url?.startsWith("workshop-resumes/")
          ) {
            const cleanPath = reg.resume_url.replace("workshop-resumes/", "");
            const { data: signed } = await supabase.storage
              .from("workshop-resumes")
              .createSignedUrl(cleanPath, 3600);
            return {
              ...reg,
              signed_resume_url: signed?.signedUrl || null,
            };
          }
          return reg;
        })
      );
      return withSignedUrls;
    }

    return [];
  },

  /**
   * Update an applicant's registration status.
   * Valid statuses (enforced by DB CHECK constraint):
   *   'selected' | 'rejected' | 'attended'
   */
  async updateRegistrationStatus(registrationId, status) {
    if (!registrationId) throw new Error("Registration ID is required");

    const VALID_STATUSES = ["selected", "rejected", "attended"];
    const normalizedStatus = (status || "").toLowerCase().trim();
    if (!VALID_STATUSES.includes(normalizedStatus)) {
      throw new Error(`Invalid registration status: "${status}". Must be one of: ${VALID_STATUSES.join(", ")}`);
    }

    const { data, error } = await supabase
      .from("workshop_registrations")
      .update({ status: normalizedStatus, updated_at: new Date().toISOString() })
      .eq("id", registrationId)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] updateRegistrationStatus error:", error);
      throw error;
    }

    return data;
  },

  /**
   * Delete an applicant registration record permanently.
   * Releases their consumed seat immediately via the database trigger.
   */
  async deleteRegistration(registrationId) {
    if (!registrationId) throw new Error("Registration ID is required");

    const { error } = await supabase
      .from("workshop_registrations")
      .delete()
      .eq("id", registrationId);

    if (error) {
      console.error("[workshopsService] deleteRegistration error:", error);
      throw error;
    }

    return true;
  },

  /**
   * Upsert all instructors for a workshop (replace-all strategy).
   * - instructors: array of { id?, name, role, bio, avatar_url, linkedin_url, is_lead }
   * - The lead instructor (is_lead=true or first) is also written to workshops.instructor_id
   * - All instructor profiles are upserted into workshop_instructors first
   * - Then workshop_instructor_links is replaced (delete + insert)
   */
  async upsertWorkshopInstructors(workshopId, instructors) {
    if (!workshopId) throw new Error("Workshop ID is required");
    if (!Array.isArray(instructors) || instructors.length === 0) {
      throw new Error("At least one instructor is required");
    }

    // 1. Upsert each instructor profile into workshop_instructors
    const savedInstructors = await Promise.all(
      instructors.map((inst) =>
        this.upsertInstructor({
          id: inst.id || undefined,
          name: inst.name,
          role: inst.role,
          bio: inst.bio,
          avatar_url: inst.avatar_url,
          linkedin_url: inst.linkedin_url,
        })
      )
    );

    // 2. Delete existing junction rows
    const { error: deleteError } = await supabase
      .from("workshop_instructor_links")
      .delete()
      .eq("workshop_id", workshopId);

    if (deleteError) {
      console.error("[workshopsService] upsertWorkshopInstructors delete error:", deleteError);
      throw deleteError;
    }

    // 3. Insert new junction rows
    const linkPayload = savedInstructors.map((saved, index) => ({
      workshop_id: workshopId,
      instructor_id: saved.id,
      display_order: index,
      is_lead: instructors[index]?.is_lead ?? (index === 0),
    }));

    const { error: insertError } = await supabase
      .from("workshop_instructor_links")
      .insert(linkPayload);

    if (insertError) {
      console.error("[workshopsService] upsertWorkshopInstructors insert error:", insertError);
      throw insertError;
    }

    // 4. Wire the lead instructor back to workshops.instructor_id
    const leadInstructor = savedInstructors[instructors.findIndex((i) => i.is_lead)] || savedInstructors[0];
    await supabase
      .from("workshops")
      .update({ instructor_id: leadInstructor.id, updated_at: new Date().toISOString() })
      .eq("id", workshopId);

    return savedInstructors;
  },

  /**
   * Fetch all workshop categories for admin dropdowns.
   */
  async getCategories() {
    const { data, error } = await supabase
      .from("workshop_categories")
      .select("id, name, slug, description, display_order")
      .order("display_order", { ascending: true });

    if (error) {
      console.error("[workshopsService] getCategories error:", error);
      throw error;
    }

    return data || [];
  },

  /**
   * Replace all agenda items for a given workshop.
   * Strategy: delete existing rows → insert ordered new items.
   * Each item: { topic, description }
   * display_order is assigned sequentially (0-indexed).
   */
  async upsertAgendaItems(workshopId, items) {
    if (!workshopId) throw new Error("Workshop ID is required");
    if (!Array.isArray(items)) throw new Error("Items must be an array");

    // Delete all existing agenda items for this workshop
    const { error: deleteError } = await supabase
      .from("workshop_agenda")
      .delete()
      .eq("workshop_id", workshopId);

    if (deleteError) {
      console.error("[workshopsService] upsertAgendaItems delete error:", deleteError);
      throw deleteError;
    }

    // Insert new items if any exist
    if (items.length === 0) return [];

    const payload = items.map((item, index) => ({
      workshop_id: workshopId,
      topic: (item.topic || "").trim(),
      description: (item.description || "").trim(),
      display_order: index,
    }));

    const { data, error: insertError } = await supabase
      .from("workshop_agenda")
      .insert(payload)
      .select();

    if (insertError) {
      console.error("[workshopsService] upsertAgendaItems insert error:", insertError);
      throw insertError;
    }

    return data || [];
  },

  /**
   * Upsert internal data for a workshop (meeting link, internal notes).
   * Uses ON CONFLICT (workshop_id) DO UPDATE via Supabase upsert.
   * This data is NEVER returned through public Workshop queries.
   */
  async upsertInternalData(workshopId, internalData) {
    if (!workshopId) throw new Error("Workshop ID is required");

    const payload = {
      workshop_id: workshopId,
      meeting_link: (internalData.meeting_link || "").trim(),
      internal_notes: (internalData.internal_notes || "").trim(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("workshop_internal_data")
      .upsert(payload, { onConflict: "workshop_id" })
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] upsertInternalData error:", error);
      throw error;
    }

    return data;
  },

  /**
   * ============================================================
   * 3. PROMOTIONAL MODAL & HIGHLIGHTS MEDIA METHODS
   * ============================================================
   */

  /**
   * Fetch eligible workshops for the global promotional modal.
   * Strictly returns active, published workshops marked with show_in_promotional_modal = true
   * that are either Upcoming or Live (never completed or cancelled).
   */
  async getPromotionalWorkshops() {
    const { data, error } = await supabase
      .from("workshops")
      .select(`
        *,
        workshop_categories (id, name, slug, description),
        workshop_instructors!workshops_instructor_id_fkey (id, name, role, bio, avatar_url, linkedin_url),
        workshop_instructor_links (display_order, is_lead, workshop_instructors (id, name, role, bio, avatar_url, linkedin_url)),
        workshop_media (*)
      `)
      .eq("is_published", true)
      .eq("status", "Published")
      .eq("show_in_promotional_modal", true)
      .is("deleted_at", null);

    if (error) {
      console.error("[workshopsService] getPromotionalWorkshops error:", error);
      return [];
    }

    const mapped = (data || []).map(mapDatabaseWorkshopToUI);

    // Keep ONLY upcoming workshops (never promote completed cohorts or undefined states)
    const eligible = mapped.filter(
      (w) => w.lifecycle_stage === "upcoming"
    );

    // Order by:
    // 1. Explicitly featured workshop first
    // 2. Nearest upcoming start date
    // 3. display_order
    eligible.sort((a, b) => {
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;

      const aTime = a.start_date_obj ? a.start_date_obj.getTime() : Infinity;
      const bTime = b.start_date_obj ? b.start_date_obj.getTime() : Infinity;
      if (aTime !== bTime) return aTime - bTime;

      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });

    return eligible;
  },

  /**
   * Update workshop promotional modal appearance setting.
   * Accepts either a boolean directly or an object `{ show_in_promotional_modal }`.
   */
  async updateWorkshopPromotionSettings(id, settings) {
    if (!id) throw new Error("Workshop ID is required");
    const val =
      typeof settings === "boolean"
        ? settings
        : Boolean(settings?.show_in_promotional_modal);

    const { data, error } = await supabase
      .from("workshops")
      .update({
        show_in_promotional_modal: val,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] updateWorkshopPromotionSettings error:", error);
      if (error.code === "PGRST116") {
        throw new Error("Update failed: You might not be authenticated as an administrator or your session expired.");
      }
      throw error;
    }
    return mapDatabaseWorkshopToUI(data);
  },

  /**
   * Update workshop post-event highlights visibility setting.
   * Accepts either a boolean directly or an object `{ show_highlights }`.
   */
  async updateWorkshopHighlightsSettings(id, settings) {
    if (!id) throw new Error("Workshop ID is required");
    const val =
      typeof settings === "boolean"
        ? settings
        : Boolean(settings?.show_highlights);

    const { data, error } = await supabase
      .from("workshops")
      .update({
        show_highlights: val,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] updateWorkshopHighlightsSettings error:", error);
      if (error.code === "PGRST116") {
        throw new Error("Update failed: You might not be authenticated as an administrator or your session expired.");
      }
      throw error;
    }
    return mapDatabaseWorkshopToUI(data);
  },

  /**
   * Fetch all media items for a given workshop (admin view).
   */
  async getWorkshopMedia(workshopId) {
    if (!workshopId) return [];
    const { data, error } = await supabase
      .from("workshop_media")
      .select("*")
      .eq("workshop_id", workshopId)
      .order("display_order", { ascending: true });

    if (error) {
      console.error("[workshopsService] getWorkshopMedia error:", error);
      throw error;
    }
    return data || [];
  },

  /**
   * Create a new media item (e.g. highlight image or video).
   */
  async createWorkshopMedia(workshopId, mediaData) {
    if (!workshopId) throw new Error("Workshop ID is required");
    // Ensure storage_path has clear semantic meaning for both images and videos
    const storagePath = (
      mediaData.storage_path ||
      mediaData.thumbnail_url ||
      mediaData.video_url ||
      mediaData.url ||
      ""
    ).trim();

    const payload = {
      workshop_id: workshopId,
      storage_path: storagePath,
      media_type: mediaData.media_type || "highlight",
      media_format: mediaData.media_format || (mediaData.video_url ? "video" : "image"),
      video_url: (mediaData.video_url || "").trim(),
      thumbnail_url: (mediaData.thumbnail_url || "").trim(),
      alt_text: (mediaData.alt_text || "").trim(),
      caption: (mediaData.caption || "").trim(),
      description: (mediaData.description || "").trim(),
      display_order: Number(mediaData.display_order ?? 0),
      is_published: mediaData.is_published !== false,
      is_featured: Boolean(mediaData.is_featured),
    };

    const { data, error } = await supabase
      .from("workshop_media")
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] createWorkshopMedia error:", error);
      throw error;
    }
    return data;
  },

  /**
   * Update an existing workshop media item.
   */
  async updateWorkshopMedia(mediaId, mediaData) {
    if (!mediaId) throw new Error("Media ID is required");
    const payload = {
      ...mediaData,
      updated_at: new Date().toISOString(),
    };
    delete payload.id;
    delete payload.workshop_id;

    const { data, error } = await supabase
      .from("workshop_media")
      .update(payload)
      .eq("id", mediaId)
      .select()
      .single();

    if (error) {
      console.error("[workshopsService] updateWorkshopMedia error:", error);
      throw error;
    }
    return data;
  },

  /**
   * Delete a workshop media item.
   */
  async deleteWorkshopMedia(mediaId) {
    if (!mediaId) throw new Error("Media ID is required");
    const { error } = await supabase
      .from("workshop_media")
      .delete()
      .eq("id", mediaId);

    if (error) {
      console.error("[workshopsService] deleteWorkshopMedia error:", error);
      throw error;
    }
    return true;
  },

  /**
   * Reorder workshop media items sequentially.
   */
  async reorderWorkshopMedia(workshopId, orderedIds) {
    if (!workshopId || !Array.isArray(orderedIds)) return;
    const updates = orderedIds.map((id, index) =>
      supabase
        .from("workshop_media")
        .update({ display_order: index, updated_at: new Date().toISOString() })
        .eq("id", id)
        .eq("workshop_id", workshopId)
    );
    await Promise.all(updates);
  },
};

export default workshopsService;
